import React, { useEffect, memo } from 'react';
import { motion } from 'framer-motion';
import { PageCTA } from '../components/PageCTA';
import { BarChart3, Users, Zap, Smartphone, Eye, Star, ShieldCheck, MessageSquare, TrendingUp, ArrowUpRight } from 'lucide-react';
export function ResultsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const metrics = [{
    icon: <BarChart3 size={32} data-id="element-760" />,
    stat: '+70%',
    title: 'Incremento en Autoridad de Marca',
    description: 'Arquitectura visual premium que justifica un pricing superior y acorta ciclos de venta.',
    metricVisual: 'bar'
  }, {
    icon: <Users size={32} data-id="element-761" />,
    title: 'Multiplicador de Adquisición',
    stat: '+3x',
    description: 'Eliminamos la fricción cognitiva para maximizar la conversión de tráfico a leads.',
    metricVisual: 'progress'
  }, {
    icon: <Zap size={32} data-id="element-762" />,
    title: 'Eficiencia de Conversión',
    stat: '100%',
    description: 'Micro-interacciones calibradas algorítmicamente para dirigir el comportamiento del usuario.',
    metricVisual: 'line'
  }, {
    icon: <Smartphone size={32} data-id="element-763" />,
    title: 'Omnicanalidad Nativa',
    stat: '100%',
    description: 'Rendimiento impecable y adaptabilidad fluida en todo el espectro de dispositivos.',
    metricVisual: 'donut'
  }, {
    icon: <TrendingUp size={32} data-id="element-764" />,
    title: 'Retorno de Inversión',
    stat: '+250%',
    description: 'Aceleración del ciclo de ventas y reducción drástica del coste de adquisición.',
    metricVisual: 'bar'
  }, {
    icon: <Star size={32} data-id="element-765" />,
    title: 'Retención de Clientes',
    stat: '+85%',
    description: 'Experiencias memorables que transforman compradores ocasionales en embajadores de marca.',
    metricVisual: 'progress'
  }];
  const benefits = [{
    icon: <Eye size={28} data-id="element-766" />,
    title: 'Dominio SEO',
    description: 'Indexación prioritaria para capturar la demanda activa.',
    metric: '+85% Visibilidad'
  }, {
    icon: <Star size={28} data-id="element-767" />,
    title: 'Impacto Cognitivo',
    description: 'Interfaces que capturan la atención en menos de 3 segundos.',
    metric: '-40% Rebote'
  }, {
    icon: <ShieldCheck size={28} data-id="element-768" />,
    title: 'Confianza Institucional',
    description: 'Infraestructura robusta que elimina el escepticismo del consumidor.',
    metric: '100% Legitimidad'
  }, {
    icon: <MessageSquare size={28} data-id="element-769" />,
    title: 'Canalización Ágil',
    description: 'Sistemas de contacto integrados que aceleran el cierre de ventas.',
    metric: '3x Velocidad'
  }, {
    icon: <TrendingUp size={28} data-id="element-770" />,
    title: 'Escalabilidad',
    description: 'Motor de generación de ingresos automatizado operando 24/7.',
    metric: 'ROI Positivo'
  }];
  const cases = [{
    type: 'Sector Gastronómico Premium',
    problem: 'Menús en PDF, reservas manuales e imagen digital que no reflejaba la calidad del local.',
    solution: 'App web para catálogos dinámicos, motor de reservas automatizado y sincronización de redes sociales.',
    result: '+40% reservas automatizadas y elevación drástica del posicionamiento de marca.',
    tags: ['PWA', 'Automatización', 'UI/UX']
  }, {
    type: 'Servicios B2B / B2C Locales',
    problem: 'Invisible en búsquedas locales. Web obsoleta con alta tasa de rebote y soporte innecesario.',
    solution: 'Reingeniería web con SEO local técnico, taxonomía clara de servicios y conversión directa vía WhatsApp.',
    result: 'Dominio en búsqueda local, reducción drástica de consultas de soporte y aumento exponencial de leads.',
    tags: ['SEO Técnico', 'Arquitectura', 'APIs']
  }, {
    type: 'Consultoría de Alta Dirección',
    problem: 'Falta de autoridad digital. Onboarding manual y percepción de valor incongruente con tarifas premium.',
    solution: 'Plataforma corporativa de autoridad con agendamiento sincronizado y hub de contenidos.',
    result: 'Automatización del embudo de captación y +30% en pricing sin impacto en conversión.',
    tags: ['Autoridad', 'Sistemas', 'Escalabilidad']
  }];
  return <div className="pt-24" data-id="element-771">
      {/* Page Header */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" data-id="element-772">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" data-id="element-773"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] bg-brand-emerald/15 blur-[120px] rounded-full pointer-events-none" data-id="element-774"></div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-775">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-776">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter" data-id="element-777">
              Métricas que Transforman tu{' '}
              <span className="text-gradient" data-id="element-778">Empresa</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 leading-relaxed font-medium" data-id="element-779">
              No evaluamos nuestro rendimiento basándonos en estética
              superficial, sino en la aceleración del crecimiento y la
              optimización operativa que inyectamos en tu modelo de negocio.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Animated Charts Section */}
      <section className="py-24 bg-[#050505] relative" data-id="element-780">
        <div className="section-divider absolute top-0" data-id="element-781"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-782"></div>

        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10" data-id="element-783">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-784">
            {/* Chart 1: Bar Chart */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="card-premium p-8 flex flex-col" data-id="element-785">
              <div className="mb-8" data-id="element-786">
                <h3 className="text-xl font-bold text-white tracking-tight" data-id="element-787">
                  Incremento en Presencia Digital
                </h3>
                <p className="text-sm text-gray-400 mt-1" data-id="element-788">
                  Crecimiento mensual sostenido
                </p>
              </div>

              <div className="flex-1 flex items-end gap-3 h-48 mt-auto relative" data-id="element-789">
                {/* Y-axis labels */}
                <div className="absolute -left-2 top-0 bottom-0 flex flex-col justify-between text-[10px] text-gray-500 font-medium py-1" data-id="element-790">
                  <span data-id="element-791">100%</span>
                  <span data-id="element-792">50%</span>
                  <span data-id="element-793">0%</span>
                </div>

                {/* Grid lines */}
                <div className="absolute left-6 right-0 top-0 bottom-0 flex flex-col justify-between pointer-events-none" data-id="element-794">
                  <div className="w-full h-px bg-white/5" data-id="element-795"></div>
                  <div className="w-full h-px bg-white/5" data-id="element-796"></div>
                  <div className="w-full h-px bg-white/10" data-id="element-797"></div>
                </div>

                <div className="flex-1 flex items-end justify-between h-full pl-6 pb-px z-10" data-id="element-798">
                  {[25, 35, 42, 58, 65, 82, 100].map((height, i) => <div key={i} className="w-[10%] flex flex-col items-center gap-2" data-id="element-799">
                      <motion.div initial={{
                    height: 0
                  }} whileInView={{
                    height: `${height}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.2 + i * 0.1,
                    ease: 'easeOut'
                  }} className={`w-full rounded-t-sm ${i === 6 ? 'bg-gradient-to-t from-brand-emerald to-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.5)]' : 'bg-gradient-to-t from-brand-emeraldDark to-brand-emerald/80'}`} data-id="element-800" />
                      <span className="text-[10px] text-gray-500 font-medium" data-id="element-801">
                        {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul'][i]}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            {/* Chart 2: Line Chart */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="card-premium p-8 flex flex-col" data-id="element-802">
              <div className="mb-8" data-id="element-803">
                <h3 className="text-xl font-bold text-white tracking-tight" data-id="element-804">
                  Tasa de Conversión
                </h3>
                <p className="text-sm text-gray-400 mt-1" data-id="element-805">
                  Tráfico a leads cualificados
                </p>
              </div>

              <div className="flex-1 relative h-48 mt-auto w-full" data-id="element-806">
                <svg viewBox="0 0 100 60" className="w-full h-full overflow-visible" preserveAspectRatio="none" data-id="element-807">
                  <defs data-id="element-808">
                    <linearGradient id="lineGradResults" x1="0" y1="0" x2="0" y2="1" data-id="element-809">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" data-id="element-810" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" data-id="element-811" />
                    </linearGradient>
                  </defs>

                  {/* Grid */}
                  <line x1="0" y1="15" x2="100" y2="15" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" data-id="element-812" />
                  <line x1="0" y1="30" x2="100" y2="30" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" data-id="element-813" />
                  <line x1="0" y1="45" x2="100" y2="45" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" data-id="element-814" />
                  <line x1="0" y1="60" x2="100" y2="60" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" data-id="element-815" />

                  {/* Fill */}
                  <motion.path initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1,
                  delay: 0.8
                }} d="M0,60 L0,50 Q15,45 25,35 T50,25 T75,15 T100,5 L100,60 Z" fill="url(#lineGradResults)" data-id="element-816" />

                  {/* Line */}
                  <motion.path initial={{
                  pathLength: 0
                }} whileInView={{
                  pathLength: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 1.5,
                  delay: 0.3,
                  ease: 'easeInOut'
                }} d="M0,50 Q15,45 25,35 T50,25 T75,15 T100,5" fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" className="drop-shadow-[0_0_5px_rgba(16,185,129,0.8)]" data-id="element-817" />

                  {/* Points */}
                  {[{
                  cx: 0,
                  cy: 50
                }, {
                  cx: 25,
                  cy: 35
                }, {
                  cx: 50,
                  cy: 25
                }, {
                  cx: 75,
                  cy: 15
                }, {
                  cx: 100,
                  cy: 5
                }].map((pt, i) => <motion.circle key={i} initial={{
                  scale: 0,
                  opacity: 0
                }} whileInView={{
                  scale: 1,
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 1.2 + i * 0.1
                }} cx={pt.cx} cy={pt.cy} r="2" fill="#fff" data-id="element-818" />)}
                </svg>
              </div>
            </motion.div>

            {/* Chart 3: Concentric Rings */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="card-premium p-8 flex flex-col" data-id="element-819">
              <div className="mb-8" data-id="element-820">
                <h3 className="text-xl font-bold text-white tracking-tight" data-id="element-821">
                  Métricas Clave
                </h3>
                <p className="text-sm text-gray-400 mt-1" data-id="element-822">
                  Auditoría de rendimiento
                </p>
              </div>

              <div className="flex-1 flex items-center justify-center mt-auto" data-id="element-823">
                <div className="relative w-40 h-40" data-id="element-824">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90" data-id="element-825">
                    {/* Background rings */}
                    <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" data-id="element-826" />
                    <circle cx="50" cy="50" r="28" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" data-id="element-827" />
                    <circle cx="50" cy="50" r="16" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" data-id="element-828" />

                    {/* Outer ring (SEO) */}
                    <motion.circle initial={{
                    strokeDasharray: '0 251.2'
                  }} whileInView={{
                    strokeDasharray: '213.5 251.2'
                  }} // 85%
                  viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    delay: 0.4,
                    ease: 'easeOut'
                  }} cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="6" strokeLinecap="round" data-id="element-829" />

                    {/* Middle ring (UX) */}
                    <motion.circle initial={{
                    strokeDasharray: '0 175.9'
                  }} whileInView={{
                    strokeDasharray: '161.8 175.9'
                  }} // 92%
                  viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    delay: 0.6,
                    ease: 'easeOut'
                  }} cx="50" cy="50" r="28" fill="none" stroke="#34d399" strokeWidth="6" strokeLinecap="round" data-id="element-830" />

                    {/* Inner ring (Speed) */}
                    <motion.circle initial={{
                    strokeDasharray: '0 100.5'
                  }} whileInView={{
                    strokeDasharray: '98.5 100.5'
                  }} // 98%
                  viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    delay: 0.8,
                    ease: 'easeOut'
                  }} cx="50" cy="50" r="16" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" data-id="element-831" />
                  </svg>

                  {/* Labels */}
                  <div className="absolute top-0 -right-12 flex flex-col gap-2" data-id="element-832">
                    <div className="flex items-center gap-2" data-id="element-833">
                      <div className="w-2 h-2 rounded-full bg-brand-emerald" data-id="element-834"></div>
                      <span className="text-[10px] text-gray-400 font-bold" data-id="element-835">
                        SEO 85%
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-836">
                      <div className="w-2 h-2 rounded-full bg-brand-emeraldLight" data-id="element-837"></div>
                      <span className="text-[10px] text-gray-400 font-bold" data-id="element-838">
                        UX 92%
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-839">
                      <div className="w-2 h-2 rounded-full bg-white" data-id="element-840"></div>
                      <span className="text-[10px] text-gray-400 font-bold" data-id="element-841">
                        SPD 98%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-24 bg-[#0a0a0a] relative" data-id="element-842">
        <div className="section-divider absolute top-0" data-id="element-843"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-844"></div>

        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10" data-id="element-845">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-846">
            {metrics.map((metric, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-gradient-to-br from-[#151515] to-[#0a0a0a] p-10 md:p-12 rounded-[2.5rem] border border-white/5 relative overflow-hidden group shadow-[0_0_30px_rgba(0,0,0,0.5)] hover:border-brand-emerald/30 transition-all duration-500" data-id="element-847">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-emerald/5 blur-[80px] rounded-full group-hover:bg-brand-emerald/15 transition-colors duration-500" data-id="element-848"></div>
                <div className="absolute inset-0 bg-grid-pattern-emerald opacity-10" data-id="element-849"></div>

                <div className="relative z-10" data-id="element-850">
                  <div className="flex justify-between items-start mb-8" data-id="element-851">
                    <div className="icon-premium" data-id="element-852">{metric.icon}</div>
                    <div className="flex items-center gap-4" data-id="element-853">
                      {/* Visual Metric Indicator */}
                      <div className="relative w-12 h-12 flex items-center justify-center" data-id="element-854">
                        {metric.metricVisual === 'bar' && <div className="flex items-end gap-1 h-8 w-full" data-id="element-855">
                            {[40, 60, 80, 100].map((h, i) => <motion.div key={i} initial={{
                        height: 0
                      }} whileInView={{
                        height: `${h}%`
                      }} transition={{
                        duration: 0.8,
                        delay: i * 0.1
                      }} className="flex-1 bg-brand-emerald rounded-sm opacity-80" data-id="element-856" />)}
                          </div>}
                        {metric.metricVisual === 'progress' && <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90" data-id="element-857">
                            <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" data-id="element-858" />
                            <motion.circle initial={{
                        strokeDasharray: '0 100'
                      }} whileInView={{
                        strokeDasharray: '80 100'
                      }} transition={{
                        duration: 1.5
                      }} cx="18" cy="18" r="16" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" data-id="element-859" />
                          </svg>}
                        {metric.metricVisual === 'line' && <svg viewBox="0 0 50 20" className="w-full h-full overflow-visible" data-id="element-860">
                            <motion.path initial={{
                        pathLength: 0
                      }} whileInView={{
                        pathLength: 1
                      }} transition={{
                        duration: 1
                      }} d="M0,20 Q10,15 20,18 T40,5 L50,0" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" data-id="element-861" />
                          </svg>}
                        {metric.metricVisual === 'donut' && <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90" data-id="element-862">
                            <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="4" data-id="element-863" />
                            <motion.circle initial={{
                        strokeDasharray: '0 100'
                      }} whileInView={{
                        strokeDasharray: '100 100'
                      }} transition={{
                        duration: 1.5
                      }} cx="18" cy="18" r="14" fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round" data-id="element-864" />
                          </svg>}
                      </div>

                      <div className="text-5xl md:text-6xl font-black text-white tracking-tighter relative" data-id="element-865">
                        {metric.stat}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite]" data-id="element-866"></div>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4 tracking-tight" data-id="element-867">
                    {metric.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed font-medium text-lg" data-id="element-868">
                    {metric.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits Deep Dive */}
      <section className="py-24 bg-[#0a0a0a] relative" data-id="element-869">
        <div className="section-divider absolute top-0" data-id="element-870"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" data-id="element-871"></div>

        <div className="container mx-auto px-6 md:px-12 max-w-5xl relative z-10" data-id="element-872">
          <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-873">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-874">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter" data-id="element-875">
                Beneficios <span className="text-gradient" data-id="element-876">tangibles</span>
              </h2>
              <p className="text-gray-400 text-xl font-medium" data-id="element-877">
                Más allá de los números, esto es lo que experimentarás en el día
                a día de tu negocio tras implementar nuestro sistema.
              </p>
            </motion.div>
          </div>

          <div className="space-y-6" data-id="element-878">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="card-premium p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center group relative overflow-hidden border-l-2 border-l-transparent hover:border-l-brand-emerald transition-all duration-500" data-id="element-879">
                <div className="absolute top-0 right-0 p-6 text-6xl font-black text-white/5 select-none pointer-events-none group-hover:text-brand-emerald/5 transition-colors" data-id="element-880">
                  0{index + 1}
                </div>
                <div className="icon-premium shrink-0 group-hover:scale-110 transition-transform duration-500 relative z-10" data-id="element-881">
                  {benefit.icon}
                </div>
                <div className="relative z-10 flex-1" data-id="element-882">
                  <h3 className="text-xl font-bold text-white mb-2 tracking-tight" data-id="element-883">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 font-medium" data-id="element-884">
                    {benefit.description}
                  </p>
                </div>
                <div className="relative z-10 shrink-0" data-id="element-885">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-emerald/10 border border-brand-emerald/20 text-brand-emerald text-xs font-bold uppercase tracking-widest" data-id="element-886">
                    {benefit.metric}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-[#050505] relative" data-id="element-887">
        <div className="section-divider absolute top-0" data-id="element-888"></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-889"></div>

        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10" data-id="element-890">
          <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-891">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-892">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter" data-id="element-893">
                Casos de <span className="text-gradient" data-id="element-894">éxito</span>
              </h2>
              <p className="text-gray-400 text-xl font-medium" data-id="element-895">
                Ejemplos reales de cómo nuestro ecosistema digital resuelve
                problemas específicos en diferentes sectores.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-896">
            {cases.map((caseStudy, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} className="card-premium flex flex-col h-full group hover:-translate-y-2 transition-all duration-500" data-id="element-897">
                <div className="h-2 w-full bg-gradient-to-r from-brand-emerald to-brand-emeraldDark" data-id="element-898"></div>
                <div className="p-8 md:p-10 flex-1 flex flex-col relative z-10" data-id="element-899">
                  <div className="flex justify-between items-start mb-8" data-id="element-900">
                    <h3 className="text-2xl font-bold text-white tracking-tight" data-id="element-901">
                      {caseStudy.type}
                    </h3>
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-brand-emerald/20 transition-colors" data-id="element-902">
                      <ArrowUpRight className="text-gray-400 group-hover:text-brand-emerald transition-colors" data-id="element-903" />
                    </div>
                  </div>

                  <div className="space-y-8 mb-10 flex-1" data-id="element-904">
                    <div data-id="element-905">
                      <span className="text-xs font-bold text-red-400 uppercase tracking-widest mb-3 block" data-id="element-906">
                        El desafío
                      </span>
                      <p className="text-base text-gray-400 leading-relaxed font-medium" data-id="element-907">
                        {caseStudy.problem}
                      </p>
                    </div>
                    <div data-id="element-908">
                      <span className="text-xs font-bold text-brand-emerald uppercase tracking-widest mb-3 block" data-id="element-909">
                        La solución Growthly
                      </span>
                      <p className="text-base text-gray-300 leading-relaxed font-medium" data-id="element-910">
                        {caseStudy.solution}
                      </p>
                    </div>
                    <div className="p-6 bg-gradient-to-br from-brand-emerald/10 to-transparent rounded-xl border border-brand-emerald/20" data-id="element-911">
                      <span className="text-xs font-bold text-white uppercase tracking-widest mb-3 block" data-id="element-912">
                        El resultado
                      </span>
                      <p className="text-base text-brand-emerald leading-relaxed font-bold" data-id="element-913">
                        {caseStudy.result}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-auto" data-id="element-914">
                    {caseStudy.tags.map((tag, i) => <span key={i} className="text-xs font-bold px-4 py-2 bg-[#1a1a1a] text-gray-300 rounded-full border border-white/10 uppercase tracking-wider group-hover:border-brand-emerald/30 group-hover:text-brand-emerald group-hover:bg-brand-emerald/5 transition-colors duration-300" data-id="element-915">
                        {tag}
                      </span>)}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <PageCTA data-id="element-916" />
    </div>;
}